// index.tsx
import {
  AnalyticsService,
  DashboardVM,
  DateFilterTypesEnum,
  MetricTypesEnum,
  OrganizationService,
  OrganizationVM,
  PropertyMetricTypesEnum,
} from "@propertelligent/client-api";
import { useCallback, useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { UserLoginData } from "../../../components/common/charts/Enums";
import apiHelper from "../../../utils/apiHelper";
import OrganizationListingCardItem from "../../../newComponents/listings/listing-cards/dashboard-organizationListingCardItem";
import DashboardCard from "../../../newComponents/Dashoard/StatsCards/dashboard-card";
import { buildTenantFooterColumns } from "./tenantFooterColumns";
import OverviewPortfolioIcon from "../../../assets/svgs/overviewPortfolioIcon";
import OverviewTeanantIcon from "../../../assets/svgs/overviewTeanantIcon";
import OverviewServiceRequestIcon from "../../../assets/svgs/overviewServiceRequestIcon";
import { useAnalyticsHook } from "../../../hooks/useAnalyticsHook";
import DonutChart from "../../../newComponents/charts/revenue-overview/revenue-overview";
import ExpenseDistribution from "../../../newComponents/charts/expense-distribution/expense-distribution";
import PerformanceOverview from "../../../newComponents/PerformanceOverviewList/performance-overview";
import SimpleSpinner from "../../../components/loader";
import CashFlowOverview from "../../../newComponents/charts/line-chart/line-chart";
import VacancyRate from "../../../newComponents/charts/vacancy-rate/vacancy-rate";
import UtilHelper from "../../../utils/UtilHelper";
import { useCurrencySymbol } from "../../../hooks/useCurrencySymbol";
import { PermissionGuard } from "../../../newComponents/PermissionGuard";
import NoAccess from "../../../pages/NoAccess";
import { AccessLevelEnum } from "@propertelligent/client-api";

interface ExpenseCategory {
  name: string;
  amount: number;
  color: string;
  formattedAmount?: string;
  formatted?: any;
}

interface CashFlowItem {
  month: string;
  revenue: number;
  expense: number;
}


const OverviewPage = () => {
  /* ------------------------------- STATES ------------------------------- */
  const TIME_RANGES = ["Last 12 Month", "Last 6 Month", "Last 3 Month", "This Month"];
  const [loading, setLoading] = useState(false);
  const [expenseLoading, setExpenseLoading] = useState(false);
  const [organizationData, setOrganizationData] = useState<OrganizationVM>({});
  const organizationId = Number(sessionStorage.getItem(UserLoginData.organization_id));
  const [dashboardData, setDashboardData] = useState<DashboardVM | null>(null);

  // ---------- Filters States----------
  const [revenueFilter, setRevenueFilter] = useState<string>("Last 12 Month");
  const [expenseFilter, setExpenseFilter] = useState<string>("Last 12 Month");
  const [perfRevFilter, setPerfRevFilter] = useState<string>("Last 12 Month");
  const [perfExpFilter, setPerfExpFilter] = useState<string>("Last 12 Month");

  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);
  const [performaceRevdata, setPerformanceRevdata] = useState<any[]>([]);
  const [perfRevLoading, setPerfRevLoading] = useState(false);

  const [cashFlowFilter, setCashFlowFilter] = useState<string>(TIME_RANGES[0]);
  const [cashFlowData, setCashFlowData] = useState<CashFlowItem[]>([]);
  const [cashFlowLoading, setCashFlowLoading] = useState<boolean>(false);


  const [vacancyFilter, setVacancyFilter] = useState<string>(TIME_RANGES[0]);
  const [vacancyData, setVacancyData] = useState<any[]>([]);
  const [vacancyLoading, setVacancyLoading] = useState<boolean>(false);

  // Revenue Overview Totals
  const [totals, setTotals] = useState({
    totalIncome: { value: 0, formatted: "0", formattedRaw: "0" },
    totalExpense: { value: 0, formatted: "0", formattedRaw: "0" },
    netIncome: { value: 0, formatted: "0", formattedRaw: "0" },
  });

  const [dataLoading, setDataLoading] = useState(false);
  const currencySymbol = useCurrencySymbol();

  /* --------------------------- FORM SETUP --------------------------- */
  const methods = useForm<OrganizationVM>({
    defaultValues: {
      name: "",
      unitNumber: "",
      phoneNumber: "",
      email: "",
      website: "",
      address: {
        streetAddress: "",
        city: "",
        state: "",
        zip: "",
      },
    },
  });

  /* --------------------------- HELPERS --------------------------- */
  const timeRangeToEnumMap = (range: string): number => {
    const map: Record<string, number> = {
      "Last 12 Month": DateFilterTypesEnum.Last12Months,
      "Last 6 Month": DateFilterTypesEnum.Last6Months,
      "Last 3 Month": DateFilterTypesEnum.Last3Months,
      "This Month": DateFilterTypesEnum.MonthToDate,
    };
    return map[range] ?? DateFilterTypesEnum.MonthToDate;
  };

  /* ----------------------- DASHBOARD DATA ----------------------- */
  const getAnalyticsDashboard = useCallback(() => {
    setLoading(true);
    apiHelper(
      AnalyticsService.getApiAnalyticsDashboard,
      { showNotification: false },
      organizationId
    )
      .then((res) => {
        if (res?.data?.success) {
          setDashboardData(res.data.success);
        }
      })
      .catch((error) => console.error("Error fetching dashboard analytics:", error))
      .finally(() => setLoading(false));
  }, [organizationId]);

  /* ---------------------- ORGANIZATION DATA --------------------- */
  const getOrganization = useCallback(() => {
    setLoading(true);
    apiHelper(
      OrganizationService.getApiOrganization1,
      { showNotification: false },
      organizationId
    )
      .then((res) => {
        if (res?.data?.success) {
          const org = res.data.success;
          setOrganizationData(org);
          methods.reset({
            name: org.name || "",
            unitNumber: org.unitNumber || "",
            phoneNumber: UtilHelper.toPhoneInputValue(
              org?.formatted?.PhoneNumber || org.phoneNumber || "",
            ),
            email: org.email || "",
            website: org.website || "",
            address: {
              streetAddress: org.address?.streetAddress || "",
              city: org.address?.city || "",
              state: org.address?.state || "",
              zip: org.address?.zip || "",
            },
          });
        }
      })
      .catch((error) => console.error("Error fetching organization:", error))
      .finally(() => setLoading(false));
  }, [organizationId, methods]);

  /* -------------------------- CASHFLOW (API) -------------------------- */
  useEffect(() => {
    const fetchTotals = async () => {
      setDataLoading(true);
      try {
        const res = await apiHelper(
          AnalyticsService.getApiCashFlowOrganizationCashflow,
          { showNotification: false },
          organizationId,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          timeRangeToEnumMap(revenueFilter),
          new Date().toISOString()
        );

        const formatted = res?.data?.success?.formatted;
        const totalIncome = res?.data?.success?.totalIncome ?? 0;
        const totalExpense = res?.data?.success?.totalExpense ?? 0;
        const netIncome = res?.data?.success?.netIncome ?? 0;

        if (formatted) {
          setTotals({
            totalIncome: { value: totalIncome, formatted: formatted.TotalIncome ?? "0", formattedRaw: formatted.TotalIncomeRaw ?? "0" },
            totalExpense: { value: totalExpense, formatted: formatted.TotalExpense ?? "0", formattedRaw: formatted.TotalExpenseRaw ?? "0" },
            netIncome: { value: netIncome, formatted: formatted.NetIncome ?? "0", formattedRaw: formatted.NetIncomeRaw ?? "0" },
          });
        }
      } catch (err) {
        console.error("Cashflow totals API error:", err);
      } finally {
        setDataLoading(false);
      }
    };

    fetchTotals();
  }, [organizationId, revenueFilter]);

  /* ----------------------- EXPENSE STATS ----------------------- */
  const getExpenseStats = useCallback(() => {
    setExpenseLoading(true);
    const monthsBack = timeRangeToEnumMap(expenseFilter);

    apiHelper(
      AnalyticsService.getApiAnalyticsOrganizationExpensebycategory,
      { showNotification: false },
      organizationId,
      undefined,          // pageSize
      undefined,          // pageNumber
      undefined,          // skipValue
      undefined,          // filterEntityTypeId
      undefined,          // filterEntityIds
      undefined,          // fullyQualifiedFilters
      undefined,          // fullyQualifiedSorts
      monthsBack,         // dateFilterDateFilterTypeId
      new Date().toISOString() // dateFilterClientDate
    )
      .then((res) => {
        const rawItems = res?.data?.success;
        if (rawItems && Array.isArray(rawItems)) {
          const categories = rawItems.map((item: any, idx: number) => ({
            name: item.formatted.ExpenseType || "Unknown",
            amount: parseFloat(item.formatted.AmountRaw.replace(/,/g, "")) || 0,
            color: ["#2563eb", "#f97316", "#059669", "#db2777", "#c026d3"][idx % 5],
            formattedAmount: item.formatted.Amount || "",
            formatted: item.formatted,
          }));
          setExpenseCategories(categories);
        } else {
          setExpenseCategories([]);
        }
      })
      .catch((err) => {
        console.error("Expense API Error:", err);
        setExpenseCategories([]);
      })
      .finally(() => setExpenseLoading(false));
  }, [organizationId, expenseFilter]);

  /* --------------------- PERFORMANCE REVENUE ------------------- */
  const getPerformanceRev = useCallback(() => {
    setPerfRevLoading(true);
    const monthsBack = timeRangeToEnumMap(perfRevFilter);
    apiHelper(
      AnalyticsService.getApiAnalytics,
      { showNotification: false },
      organizationId,
      MetricTypesEnum.Top5,
      PropertyMetricTypesEnum.Revenue,
      monthsBack,
      new Date().toISOString() // dateFilterClientDate
    )
      .then((res) => setPerformanceRevdata(res?.data?.success || []))
      .catch((err) => console.error("Performance Revenue API Error:", err))
      .finally(() => setPerfRevLoading(false));
  }, [organizationId, perfRevFilter]);

  /* ---------- CASHFLOW (CHART) ---------- */
  const fetchCashFlow = useCallback(async () => {
    setCashFlowLoading(true);
    const monthsBack = timeRangeToEnumMap(cashFlowFilter);

    try {
      const res = await apiHelper(
        AnalyticsService.getApiCashFlowOrganizationCashflow,
        { showNotification: false },
        organizationId,
        undefined,          // pageSize
        undefined,          // pageNumber
        undefined,          // skipValue
        undefined,          // filterEntityTypeId
        undefined,          // filterEntityIds
        undefined,          // fullyQualifiedFilters
        undefined,          // fullyQualifiedSorts
        monthsBack,         // dateFilterDateFilterTypeId
        new Date().toISOString() // dateFilterClientDate
      );

      const successData = res?.data?.success;
      if (successData?.cashFlowItems && Array.isArray(successData.cashFlowItems)) {
        const formatted = successData.cashFlowItems.map((item: any) => ({
          month: item.month ?? "",
          revenue: Number(item.income ?? 0),
          expense: Number(item.expense ?? 0),
        }));
        setCashFlowData(formatted);
      } else {
        setCashFlowData([]);
      }
    } catch (err) {
      console.error("CashFlow API Error:", err);
      setCashFlowData([]);
    } finally {
      setCashFlowLoading(false);
    }
  }, [organizationId, cashFlowFilter]);


  /* ---------- VACANCY RATE ---------- */
  const fetchVacancyRate = useCallback(async () => {
    setVacancyLoading(true);
    const monthsBack = timeRangeToEnumMap(vacancyFilter);

    try {
      const res = await apiHelper(
        AnalyticsService.getApiAnalyticsOrganizationVacancyrates,
        { showNotification: false },
        organizationId,
        monthsBack
      );

      if (res?.data?.success?.dataSet && Array.isArray(res.data.success.dataSet)) {
        const formatted = res.data.success.dataSet
          .map((item: any) => ({
            month: new Date(item.year, item.month - 1).toLocaleString("default", {
              month: "short",
            }),
            occupied: item.occupiedUnits || 0,
            unoccupied: item.unOccupiedUnits || 0,
            // keep raw values for reliable sorting
            _year: item.year,
            _month: item.month,
          }))
          // Sort oldest → newest
          .sort((a: any, b: any) => a._year - b._year || a._month - b._month)
          // drop helper fields
          .map(({ _year, _month, ...rest }) => rest);

        setVacancyData(formatted);
      } else {
        setVacancyData([]);
      }
    } catch (err) {
      console.error("Vacancy Rate API Error:", err);
      setVacancyData([]);
    } finally {
      setVacancyLoading(false);
    }
  }, [organizationId, vacancyFilter]);



  /* ---------------------------- EFFECTS -------------------------- */
  useEffect(() => {
    getOrganization();
    getAnalyticsDashboard();
  }, [getOrganization, getAnalyticsDashboard]);

  useEffect(() => {
    getExpenseStats();
  }, [getExpenseStats]);

  useEffect(() => {
    getPerformanceRev();
  }, [getPerformanceRev]);

  /* ---------- Trigger on filter change ---------- */
  useEffect(() => {
    fetchCashFlow();
  }, [fetchCashFlow]);

  useEffect(() => {
    fetchVacancyRate();
  }, [fetchVacancyRate]);

  /* --------------------------- CARD DATA -------------------------- */
  const portfolioData = {
    icon: <OverviewPortfolioIcon />,
    mainValue: dashboardData?.formatted?.PortfolioValue || 0,
    rawValue: dashboardData?.formatted?.PortfolioValueRaw || 0,
    mainLabel: "Total Portfolio Value",
    footerColumns: [
      { value: dashboardData?.formatted?.NumberOfProperties || 0, label: "Properties", valueRaw: dashboardData?.formatted?.NumberOfPropertiesRaw || 0 },
      { value: dashboardData?.formatted?.NumberOfUnits || 0, label: "Total Units", valueRaw: dashboardData?.formatted?.NumberOfUnitsRaw || 0 },
      { value: dashboardData?.formatted?.NumberOfVacantUnits || 0, label: "Vacant Units", valueRaw: dashboardData?.formatted?.NumberOfVacantUnitsRaw || 0 },
    ],
  };

  const tenantsData = {
    icon: <OverviewTeanantIcon />,
    mainValue: dashboardData?.formatted?.NumberOfTenants || 0,
    rawValue: dashboardData?.formatted?.NumberOfTenantsRaw || 0,
    mainLabel: "Total Tenants",
    footerColumns: buildTenantFooterColumns(dashboardData),
  };


  const serviceRequestData = {
    icon: <OverviewServiceRequestIcon />,
    mainValue: dashboardData?.formatted?.OpenServiceRequests || dashboardData?.openServiceRequests || 0,
    rawValue: dashboardData?.formatted?.OpenServiceRequestsRaw || dashboardData?.openServiceRequests || 0,
    mainLabel: "Open Service Requests",
    footerColumns: [
      {
        value: dashboardData?.formatted?.ServiceRequestsEstimatedExpenses
          ? `${dashboardData?.formatted?.ServiceRequestsEstimatedExpenses}`
          : "---",
        label: "Estimated Expense",
        valueRaw: dashboardData?.formatted?.ServiceRequestsEstimatedExpensesRaw || 0,
      },
      {
        value: dashboardData?.formatted?.ServiceRequestsPaymentPending
          ? `${dashboardData?.formatted?.ServiceRequestsPaymentPending}`
          : "---",
        valueRaw: dashboardData?.formatted?.ServiceRequestsPaymentPendingRaw || 0,
        label: "Payment Pending",
      },
    ],
  };

  /* ---------------------------- RENDER ---------------------------- */
  return (
    <PermissionGuard permission="OrganizationDashboard" level={AccessLevelEnum.ReadOnly} fallback={<NoAccess />}>
      <div className="basic_details organization-content-area">
        {loading ? (
        <div className="loading-card">
          <SimpleSpinner myClassName="Icon40 mt-5" />
        </div>
      ) : (
        <>
          <FormProvider {...methods}>
            <OrganizationListingCardItem
              listingData={organizationData}
              className="no-radius"
              onUpdate={getOrganization}
            />
          </FormProvider>

          <div className="dashboard-stats-container">
            {/* Top 3 Cards - Full Width */}
            <div className="dashboard-cards-grid">
              <DashboardCard {...portfolioData} isCurrency={true} isSubCurrency={false} />
              <DashboardCard {...tenantsData} isSubCurrency={false} />
              <DashboardCard {...serviceRequestData} isSubCurrency={true} />
            </div>

            {/* Row 1: Revenue + Expense Distribution */}
            <div className="charts-grid two-columns">
              <DonutChart
                totalName="Total Revenue"
                heading="Revenue Overview"
                data={[
                  {
                    title: "Net Profit",
                    value: totals.netIncome.value,
                    formattedValue: totals.netIncome.formatted,
                    valueRaw: totals.netIncome.formattedRaw,
                    color: "#1A56DB"
                  },
                  {
                    title: "Expense",
                    value: totals.totalExpense.value,
                    formattedValue: totals.totalExpense.formatted,
                    valueRaw: totals.totalExpense.formattedRaw,
                    color: "#FF5A1F"
                  },
                ]}
                valueTotal={totals.totalIncome.formatted}
                valueTotalRaw={totals.totalIncome.formattedRaw}
                symbolPosition="before"
                isAlertStrip={false}
                onTimeRangeChange={setRevenueFilter}
                isLoading={dataLoading}
              />

              <ExpenseDistribution
                categories={expenseCategories}
                heading="Expense Distribution"
                setCat_MonthValue={setExpenseFilter}
                defaultTimeRange={expenseFilter}
                isLoading={expenseLoading}
              />
            </div>

            {/* Row 2: CashFlow + Vacancy Rate */}
            <div className="charts-grid two-columns">
              <CashFlowOverview
                data={cashFlowData}
                isLoading={cashFlowLoading}
                timeRanges={TIME_RANGES}
                selectedTimeRange={cashFlowFilter}
                onTimeRangeChange={setCashFlowFilter}
                revenueColor="#1A56DB"
                expenseColor="#FF5A1F"
              />

              <VacancyRate
                timeRanges={TIME_RANGES}
                selectedTimeRange={vacancyFilter}
                onTimeRangeChange={setVacancyFilter}
                isLoading={vacancyLoading}
                data={vacancyData}
              />
            </div>

            {/* Row 3: Performance Revenue */}
            <div className="charts-grid two-columns">
              <PerformanceOverview
                onTimeRangeChange={setPerfRevFilter}
                dateFilter={perfRevFilter}
                listings={performaceRevdata}
                title="Performance Overview (Revenue)"
                isLoading={perfRevLoading}
              />
            </div>
          </div>
        </>
      )}
      </div>
    </PermissionGuard>
  );
};

export default OverviewPage;